import { Avatar, toast } from '@clareefai/ui';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Can } from '../../contexts/AbilityContext';

import {
  CheckCircle,
  Copy,
  Download,
  Eye,
  Grid,
  List,
  Phone,
  Plus,
  Search,
  Send,
  Settings,
  Star,
  Target
} from 'lucide-react';
import OneToOneCallWidget from '../../components/calls/OneToOneCallWidget';
import PageTitle from '../../components/shared/PageTitle';
import { promoterApi, useGetPromotersQuery } from '../../store/promoterApi';
import {
  useApproveReferenceCallSubmissionMutation,
  useGetReferenceCallSubmissionsQuery,
  useGetTestimonialRequestsQuery,
  useRejectReferenceCallSubmissionMutation,
} from '../../store/vendorApi';

const OneToOneCalls: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'our_requests' | 'promoter_submissions' | 'activated' | 'widget'>('our_requests');
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Fetch our 1-to-1 call requests from API
  const { data: oneOnOneRequests = [], isLoading: loadingRequests } = useGetTestimonialRequestsQuery({ type: 'ONE_ON_ONE' });
  const ourCallRequests = useMemo(() => {
    return oneOnOneRequests.map((request) => ({
      id: request.id,
      promoterName: request.promoterName,
      promoterCompany: request.promoterCompany || '',
      promoterAvatar: undefined,
      requestDate: request.requestedAt,
      status: request.status === 'VENDOR_REQUESTED' ? 'sent' :
        request.status === 'PROMOTER_APPROVED' ? 'accepted' :
          request.status === 'VENDOR_APPROVED' ? 'approved' :
            request.status === 'REJECTED' ? 'declined' : 'pending',
      emailSent: true,
      reminderCount: 0,
      lastReminder: null,
      product: request.productId || '',
      conversionValue: 0,
      notes: request.rejectionReason || '',
      type: 'our_request' as const
    }));
  }, [oneOnOneRequests]);

  // Reference call submissions (promoter-submitted frequency, vendor approves)
  const { data: referenceCallSubmissions = [], isLoading: loadingSubmissions } = useGetReferenceCallSubmissionsQuery({ status: 'PENDING' });
  const [approveSubmission, { isLoading: isApprovingSubmission }] = useApproveReferenceCallSubmissionMutation();
  const [rejectSubmission, { isLoading: isRejectingSubmission }] = useRejectReferenceCallSubmissionMutation();
  const isSubmissionActionLoading = isApprovingSubmission || isRejectingSubmission;

  // Search + status filter (our-requests tab)
  const allOurRequests = ourCallRequests.filter((request) => {
    if (filterStatus !== 'all' && request.status !== filterStatus) return false;
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (
      request.promoterName.toLowerCase().includes(term) ||
      request.promoterCompany.toLowerCase().includes(term)
    );
  });
  const allPromoterSubmissions = referenceCallSubmissions;

  const mapReferenceFrequencyToBadge = (value: string): string => {
    const map: Record<string, string> = {
      '1_week': 'weekly',
      '2_month': 'bi-monthly',
      '3_month': '3x/month',
      '1_month': 'monthly'
    };
    return map[value] ?? 'monthly';
  };

  // Activated = promoters with approved reference call frequency (from submission approval or 1-to-1 request)
  const { data: promotersList = [], isLoading: loadingPromoters } = useGetPromotersQuery();
  const allActivatedPromoters = useMemo(() => {
    return promotersList
      .filter((p) => p.referenceCallFrequency)
      .map((p) => {
        const reviewTestimonial = p.testimonials?.find((t) => t.type === 'REVIEW');
        // Count completed calls: API returns "past" for COMPLETED; meeting_ended_at is the definitive indicator
        const completedMeetings =
          p.meetings?.filter(
            (m) =>
              m.meeting_ended_at ||
              m.status === 'COMPLETED' ||
              m.status === 'completed'
          ) ?? [];
        const totalMeetings = p.meetings?.length ?? 0;
        const successRate = totalMeetings > 0 ? Math.round((completedMeetings.length / totalMeetings) * 100) : 0;
        const frequencyForBadge = mapReferenceFrequencyToBadge(p.referenceCallFrequency ?? '');
        return {
          id: p.id,
          promoterName: `${p.firstName} ${p.lastName}`,
          promoterCompany: p.company ?? '',
          promoterAvatar: p.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.firstName + ' ' + p.lastName)}&background=22c55e&color=fff`,
          frequency: frequencyForBadge,
          product: p.testimonials?.[0]?.productId ?? '',
          sector: p.industrySectors?.[0] ?? '',
          rating: reviewTestimonial?.rating ?? 0,
          review: reviewTestimonial?.content ?? '',
          activatedDate: p.updatedAt ?? p.createdAt ?? new Date().toISOString(),
          totalCalls: completedMeetings.length,
          successRate,
          availability: { days: [], timeSlots: [], timezone: p.timezone ?? '' }
        };
      });
  }, [promotersList]);

  const formatReferenceCallFrequency = (value: string) => {
    const map: Record<string, string> = {
      '1_week': t('oneToOneCalls.frequency.perWeek'),
      '2_month': t('oneToOneCalls.frequency.twoPerMonth'),
      '3_month': t('oneToOneCalls.frequency.threePerMonth'),
      '1_month': t('oneToOneCalls.frequency.onePerMonth')
    };
    return map[value] ?? value;
  };

  // The promoters list lives on a different RTK Query slice (promoterApi), so the
  // vendorApi mutation's 'Promoter' tag invalidation never reaches it — invalidate
  // it explicitly so the Activated tab (and prospect creation) refresh immediately.
  const refreshPromoters = () => {
    dispatch(promoterApi.util.invalidateTags(['Promoter']));
  };

  const handleApproveSubmission = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await approveSubmission(id).unwrap();
      refreshPromoters();
      toast.success(t('oneToOneCalls.toasts.approved'));
    } catch (err) {
      console.error('Failed to approve submission:', err);
      toast.error(t('oneToOneCalls.toasts.approveError'));
    }
  };

  const handleRejectSubmission = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await rejectSubmission({ id }).unwrap();
      refreshPromoters();
      toast.success(t('oneToOneCalls.toasts.rejected'));
    } catch (err) {
      console.error('Failed to reject submission:', err);
      toast.error(t('oneToOneCalls.toasts.rejectError'));
    }
  };

  const getSubmissionStatusBadge = (status: string) => {
    const badges = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      pending_review: 'bg-yellow-100 text-yellow-800',
      APPROVED: 'bg-green-100 text-green-800',
      approved: 'bg-green-100 text-green-800',
      editing_required: 'bg-orange-100 text-orange-800',
      REJECTED: 'bg-red-100 text-red-800',
      rejected: 'bg-red-100 text-red-800',
      activated: 'bg-purple-100 text-purple-800'
    };

    const labelKeys: Record<string, string> = {
      PENDING: 'oneToOneCalls.status.pendingReview',
      pending_review: 'oneToOneCalls.status.pendingReview',
      APPROVED: 'oneToOneCalls.status.approved',
      approved: 'oneToOneCalls.status.approved',
      editing_required: 'oneToOneCalls.status.editingRequired',
      REJECTED: 'oneToOneCalls.status.rejected',
      rejected: 'oneToOneCalls.status.rejected',
      activated: 'oneToOneCalls.status.activated'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800'}`}>
        {labelKeys[status] ? t(labelKeys[status]) : status}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      sent: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      declined: 'bg-red-100 text-red-800'
    };

    const labelKeys: Record<string, string> = {
      sent: 'oneToOneCalls.status.sent',
      accepted: 'oneToOneCalls.status.accepted',
      pending: 'oneToOneCalls.status.pending',
      declined: 'oneToOneCalls.status.declined'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status as keyof typeof badges]}`}>
        {labelKeys[status] ? t(labelKeys[status]) : status}
      </span>
    );
  };

  const getFrequencyBadge = (frequency: string) => {
    const badges: Record<string, string> = {
      weekly: 'bg-green-100 text-green-800',
      'bi-weekly': 'bg-teal-100 text-teal-800',
      monthly: 'bg-blue-100 text-blue-800',
      'bi-monthly': 'bg-purple-100 text-purple-800',
      quarterly: 'bg-orange-100 text-orange-800'
    };

    const labelKeys: Record<string, string> = {
      weekly: 'oneToOneCalls.frequency.weekly',
      'bi-weekly': 'oneToOneCalls.frequency.biWeekly',
      monthly: 'oneToOneCalls.frequency.monthly',
      'bi-monthly': 'oneToOneCalls.frequency.biMonthly',
      quarterly: 'oneToOneCalls.frequency.quarterly'
    };

    const style = badges[frequency] ?? 'bg-gray-100 text-gray-800';
    const label = labelKeys[frequency] ? t(labelKeys[frequency]) : frequency;

    return (
      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${style}`}>
        {label}
      </span>
    );
  };

  const handleActivatedPromoterClick = (promoter: { id: string }) => {
    navigate(`/promoters/${promoter.id}`);
  };

  const handleRequestClick = (request: any) => {
    navigate(`/testimonial-requests/${request.id}`);
  };

  const renderLoading = () => (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="py-12 flex items-center justify-center gap-3 text-gray-500">
        <div className="w-5 h-5 border-2 border-gray-300 border-t-[#4339A0] rounded-full animate-spin" />
        <span>{t('oneToOneCalls.loading')}</span>
      </div>
    </div>
  );

  // Compact inline stats shown top-right of the page header, switched by the
  // active tab — mirrors the Reviews Management layout for cross-page consistency.
  // The promoter-submissions and widget tabs have no stats, so this renders nothing.
  const renderInlineStats = () => {
    const stats = activeTab === 'our_requests'
      ? [
          { value: ourCallRequests.length, color: 'text-blue-600', label: t('oneToOneCalls.stats.totalRequestsSent') },
          { value: ourCallRequests.filter(r => r.status === 'accepted').length, color: 'text-green-600', label: t('oneToOneCalls.stats.accepted') },
          { value: ourCallRequests.filter(r => r.status === 'pending').length, color: 'text-yellow-600', label: t('oneToOneCalls.stats.pending') },
          { value: ourCallRequests.filter(r => r.status === 'declined').length, color: 'text-red-600', label: t('oneToOneCalls.stats.declined') },
        ]
      : activeTab === 'activated'
      ? [
          { value: allActivatedPromoters.length, color: 'text-green-600', label: t('oneToOneCalls.stats.activated') },
          { value: allActivatedPromoters.reduce((sum, p) => sum + p.totalCalls, 0), color: 'text-blue-600', label: t('oneToOneCalls.stats.totalCalls') },
          { value: `${allActivatedPromoters.length ? Math.round(allActivatedPromoters.reduce((sum, p) => sum + p.successRate, 0) / allActivatedPromoters.length) : 0}%`, color: 'text-purple-600', label: t('oneToOneCalls.stats.avgSuccessRate') },
          { value: allActivatedPromoters.filter(p => p.frequency === 'weekly').length, color: 'text-orange-600', label: t('oneToOneCalls.stats.weeklyAvailable') },
        ]
      : [];
    if (stats.length === 0) return null;
    return (
      <div className="flex items-center gap-5 sm:gap-6">
        {stats.map((s, i) => (
          <div key={i} className="text-center">
            <div className={`text-xl font-bold ${s.color} leading-tight tabular-nums`}>{s.value}</div>
            <div className="text-xs text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderOurRequests = () => loadingRequests ? renderLoading() : (
    <div className="space-y-6">
      {/* Our requests management */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('oneToOneCalls.ourRequests.title', { count: ourCallRequests.length })}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('oneToOneCalls.ourRequests.description')}</p>
          </div>

          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('oneToOneCalls.search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="all">{t('oneToOneCalls.filter.allStatuses')}</option>
              <option value="sent">{t('oneToOneCalls.status.sent')}</option>
              <option value="accepted">{t('oneToOneCalls.status.accepted')}</option>
              <option value="pending">{t('oneToOneCalls.status.pending')}</option>
              <option value="declined">{t('oneToOneCalls.status.declined')}</option>
            </select>

            <Can I="create" a="Promoter">
              <button
                onClick={() => navigate('/request-1to1-call')}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>{t('oneToOneCalls.buttons.sendNew')}</span>
              </button>
            </Can>
          </div>
        </div>

        {/* Requests grid or list */}
        {viewMode === 'list' ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.promoter')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.product')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.requestDate')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.dealValue')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.status')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allOurRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRequestClick(request)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <Avatar src={request.promoterAvatar} name={request.promoterName} size="sm" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{request.promoterName}</div>
                          <div className="text-xs text-gray-500">{request.promoterCompany}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{request.product}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{new Date(request.requestDate).toLocaleDateString('en-US')}</td>
                    <td className="px-4 py-3 text-sm font-medium text-green-600">€{request.conversionValue.toLocaleString()}</td>
                    <td className="px-4 py-3">{getStatusBadge(request.status)}</td>
                    <td className="px-4 py-3">
                      <button onClick={(e) => { e.stopPropagation(); handleRequestClick(request); }} className="text-blue-600 hover:text-blue-800 text-sm font-medium">{t('oneToOneCalls.buttons.view')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allOurRequests.map((request) => (
              <div
                key={request.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleRequestClick(request)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start space-x-3">
                    <Avatar
                      src={request.promoterAvatar}
                      name={request.promoterName}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 text-sm truncate">{request.promoterName}</h4>
                      <p className="text-xs text-gray-600 truncate">{request.promoterCompany}</p>
                    </div>
                  </div>
                  {getStatusBadge(request.status)}
                </div>

                <div className="space-y-2 mb-3 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('oneToOneCalls.fields.product')}:</span>
                    <span className="font-medium text-gray-700 truncate ml-1">{request.product}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('oneToOneCalls.fields.requestDate')}:</span>
                    <span className="font-medium text-gray-700">{new Date(request.requestDate).toLocaleDateString('en-US')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('oneToOneCalls.fields.dealValue')}:</span>
                    <span className="font-medium text-green-600">€{request.conversionValue.toLocaleString()}</span>
                  </div>
                  {request.reminderCount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('oneToOneCalls.fields.reminders')}:</span>
                      <span className="font-medium text-orange-600">{request.reminderCount}</span>
                    </div>
                  )}
                </div>

                {request.notes && (
                  <div className="bg-gray-50 border border-gray-200 rounded p-2 mb-3">
                    <p className="text-xs text-gray-700 line-clamp-2">{request.notes}</p>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRequestClick(request);
                    }}
                    className="flex-1 text-blue-600 hover:text-blue-800 text-xs font-medium py-1 px-2 border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                  >
                    {t('oneToOneCalls.buttons.view')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderPromoterSubmissions = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900">{t('oneToOneCalls.promoterSubmissions.title', { count: allPromoterSubmissions.length })}</h3>
          <p className="text-sm text-gray-600 mt-1">{t('oneToOneCalls.promoterSubmissions.description')}</p>
        </div>

        {loadingSubmissions ? (
          <div className="py-12 text-center text-gray-500">{t('oneToOneCalls.promoterSubmissions.loading')}</div>
        ) : allPromoterSubmissions.length === 0 ? (
          <div className="py-12 text-center text-gray-500">{t('oneToOneCalls.promoterSubmissions.empty')}</div>
        ) : viewMode === 'list' ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.promoter')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.frequency')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.timezone')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.submitted')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allPromoterSubmissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium text-gray-900 text-sm">{submission.promoterName}</div>
                        <div className="text-xs text-gray-500">{submission.promoterCompany ?? t('oneToOneCalls.fields.dash')}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatReferenceCallFrequency(submission.reference_call_frequency)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{submission.timezone ?? t('oneToOneCalls.fields.dash')}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{new Date(submission.submittedAt).toLocaleDateString('en-US')}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => handleApproveSubmission(e, submission.id)}
                          disabled={isSubmissionActionLoading}
                          className="text-sm font-medium py-1.5 px-3 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          {t('oneToOneCalls.buttons.approve')}
                        </button>
                        <button
                          onClick={(e) => handleRejectSubmission(e, submission.id)}
                          disabled={isSubmissionActionLoading}
                          className="text-sm font-medium py-1.5 px-3 rounded border border-red-600 text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          {t('oneToOneCalls.buttons.reject')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {allPromoterSubmissions.map((submission) => (
              <div key={submission.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-900">{submission.promoterName}</h4>
                    <p className="text-sm text-gray-600">{submission.promoterCompany ?? t('oneToOneCalls.fields.dash')}</p>
                  </div>
                  {getSubmissionStatusBadge(submission.status)}
                </div>
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('oneToOneCalls.fields.frequency')}:</span>
                    <span className="font-medium text-gray-700">{formatReferenceCallFrequency(submission.reference_call_frequency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('oneToOneCalls.fields.timezone')}:</span>
                    <span className="font-medium text-gray-700">{submission.timezone ?? t('oneToOneCalls.fields.dash')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('oneToOneCalls.fields.submitted')}:</span>
                    <span className="font-medium text-gray-700">{new Date(submission.submittedAt).toLocaleDateString('en-US')}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => handleApproveSubmission(e, submission.id)}
                    disabled={isSubmissionActionLoading}
                    className="flex-1 text-sm font-medium py-2 px-3 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    {t('oneToOneCalls.buttons.approve')}
                  </button>
                  <button
                    onClick={(e) => handleRejectSubmission(e, submission.id)}
                    disabled={isSubmissionActionLoading}
                    className="flex-1 text-sm font-medium py-2 px-3 rounded border border-red-600 text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    {t('oneToOneCalls.buttons.reject')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderActivated = () => loadingPromoters ? renderLoading() : (
    <div className="space-y-6">
      {/* Activated promoters */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('oneToOneCalls.activated.title', { count: allActivatedPromoters.length })}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('oneToOneCalls.activated.description')}</p>
          </div>

          <div className="flex items-center space-x-4">
            <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm">
              <option value="all">{t('oneToOneCalls.filter.allFrequencies')}</option>
              <option value="weekly">{t('oneToOneCalls.frequency.weekly')}</option>
              <option value="bi-weekly">{t('oneToOneCalls.frequency.biWeekly')}</option>
              <option value="monthly">{t('oneToOneCalls.frequency.monthly')}</option>
              <option value="bi-monthly">{t('oneToOneCalls.frequency.biMonthly')}</option>
              <option value="quarterly">{t('oneToOneCalls.frequency.quarterly')}</option>
            </select>

            <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm">
              <option value="all">{t('oneToOneCalls.filter.allSectors')}</option>
              <option value="healthcare">{t('oneToOneCalls.filter.healthcare')}</option>
              <option value="technology">{t('oneToOneCalls.filter.technology')}</option>
              <option value="marketing">{t('oneToOneCalls.filter.marketing')}</option>
              <option value="finance">{t('oneToOneCalls.filter.finance')}</option>
            </select>

            <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
              <Download className="w-4 h-4" />
              <span>{t('oneToOneCalls.buttons.export')}</span>
            </button>
          </div>
        </div>

        {/* Activated promoters grid or list */}
        {viewMode === 'list' ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.promoter')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.product')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.frequency')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.rating')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.totalCalls')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oneToOneCalls.fields.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allActivatedPromoters.map((promoter) => (
                  <tr key={promoter.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleActivatedPromoterClick(promoter)}>
                    <td className="px-4 py-3 max-w-[200px]">
                      <div className="flex items-center space-x-3 min-w-0">
                        <Avatar src={promoter.promoterAvatar} name={promoter.promoterName} size="sm" className="border-2 border-green-300 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-900 text-sm truncate" title={promoter.promoterName}>{promoter.promoterName}</div>
                          <div className="text-xs text-gray-500 truncate" title={promoter.promoterCompany}>{promoter.promoterCompany}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-[140px] truncate" title={promoter.product}>{promoter.product}</td>
                    <td className="px-4 py-3">{getFrequencyBadge(promoter.frequency)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1">
                        {[...Array(promoter.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                        ))}
                        <span className="text-sm text-gray-600">({promoter.rating}/5)</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{promoter.totalCalls}</td>
                    <td className="px-4 py-3">
                      <button onClick={(e) => { e.stopPropagation(); handleActivatedPromoterClick(promoter); }} className="text-green-600 hover:text-green-800 text-sm font-medium">{t('oneToOneCalls.buttons.view')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allActivatedPromoters.map((promoter) => (
              <div
                key={promoter.id}
                className="border border-green-200 rounded-lg p-6 bg-green-50 hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
                onClick={() => handleActivatedPromoterClick(promoter)}
              >
                <div className="flex items-start justify-between mb-4 min-w-0 gap-2">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <Avatar
                      src={promoter.promoterAvatar}
                      name={promoter.promoterName}
                      size="md"
                      className="border-2 border-green-300 flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold text-gray-900 truncate" title={promoter.promoterName}>{promoter.promoterName}</h4>
                      <p className="text-sm text-gray-600 truncate" title={promoter.promoterCompany}>{promoter.promoterCompany}</p>
                      <p className="text-xs text-gray-500 mt-1 truncate" title={promoter.sector}>{promoter.sector}</p>
                    </div>
                  </div>

                  <div className="flex flex-col items-end space-y-1 flex-shrink-0">
                    <Phone className="w-5 h-5 text-green-500" />
                    {getFrequencyBadge(promoter.frequency)}
                  </div>
                </div>

                <div className="mb-4 min-w-0">
                  <div className="flex items-center space-x-1 mb-2">
                    {[...Array(promoter.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current flex-shrink-0" />
                    ))}
                    <span className="text-sm text-gray-600 ml-1">({promoter.rating}/5)</span>
                  </div>
                  <p className="text-sm text-gray-700 line-clamp-2 italic break-words">"{promoter.review}"</p>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4 text-xs text-center">
                  <div className="bg-white rounded p-2">
                    <div className="font-bold text-blue-600">{promoter.totalCalls}</div>
                    <div className="text-gray-500">{t('oneToOneCalls.fields.totalCalls')}</div>
                  </div>
                  <div className="bg-white rounded p-2">
                    <div className="font-bold text-green-600">{promoter.successRate}%</div>
                    <div className="text-gray-500">{t('oneToOneCalls.activated.successRate')}</div>
                  </div>
                </div>

                <div className="space-y-2 mb-4 text-xs min-w-0">
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-500 flex-shrink-0">{t('oneToOneCalls.fields.product')}:</span>
                    <span className="font-medium text-gray-700 truncate" title={promoter.product}>{promoter.product}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-500 flex-shrink-0">{t('oneToOneCalls.fields.activated')}:</span>
                    <span className="font-medium text-gray-700">{new Date(promoter.activatedDate).toLocaleDateString('en-US')}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleActivatedPromoterClick(promoter);
                    }}
                    className="text-green-600 hover:text-green-800 text-sm font-medium py-2 px-3 border border-green-200 rounded hover:bg-green-100 transition-colors flex items-center space-x-1"
                  >
                    <Eye className="w-4 h-4" />
                    <span>{t('oneToOneCalls.buttons.view')}</span>
                  </button>
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium py-2 px-3 border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderWidget = () => (
    <div className="space-y-6">
      {/* Widget Configuration */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('oneToOneCalls.widget.title')}</h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Widget Configuration */}
          <div className="space-y-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-4">{t('oneToOneCalls.widget.configuration')}</h4>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('oneToOneCalls.widget.selectPromoter')}</label>
                  <select className="w-full p-2 border border-gray-300 rounded-lg">
                    <option>Marie Leroy - Digital Agency</option>
                    <option>Antoine Moreau - E-Commerce Plus</option>
                    <option>Sophie Durand - Innovation Lab</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('oneToOneCalls.widget.widgetTitle')}</label>
                  <input
                    type="text"
                    defaultValue={t('oneToOneCalls.widget.defaultWidgetTitle')}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('oneToOneCalls.widget.widgetDescription')}</label>
                  <textarea
                    rows={3}
                    defaultValue={t('oneToOneCalls.widget.defaultWidgetDescription')}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('oneToOneCalls.widget.buttonText')}</label>
                  <input
                    type="text"
                    defaultValue={t('oneToOneCalls.widget.defaultButtonText')}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* Embed code */}
            <div>
              <h4 className="font-medium text-gray-900 mb-4">{t('oneToOneCalls.widget.embedCode')}</h4>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm">
                <code>{`<script src="https://widget.clareefai.com/1to1-calls.js"></script>
<div id="clareefai-1to1-widget"
     data-company="avicenna"
     data-promoter="marie-leroy">
</div>`}</code>
              </div>
              <button className="mt-2 flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <Copy className="w-4 h-4" />
                <span>{t('oneToOneCalls.buttons.copyCode')}</span>
              </button>
            </div>
          </div>

          {/* Widget Preview */}
          <div className="space-y-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-4">{t('oneToOneCalls.widget.preview')}</h4>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
                <OneToOneCallWidget />
              </div>
            </div>

            {/* Widget Analytics */}
            <div>
              <h4 className="font-medium text-gray-900 mb-4">{t('oneToOneCalls.widget.analytics')}</h4>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">347</div>
                  <div className="text-sm text-blue-700">{t('oneToOneCalls.widget.widgetViews')}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">23</div>
                  <div className="text-sm text-green-700">{t('oneToOneCalls.widget.callsRequested')}</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">18</div>
                  <div className="text-sm text-purple-700">{t('oneToOneCalls.widget.callsCompleted')}</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">6.6%</div>
                  <div className="text-sm text-orange-700">{t('oneToOneCalls.widget.conversionRate')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageTitle title={t('oneToOneCalls.pageTitle')} />
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('oneToOneCalls.heading')}</h1>
          <p className="text-gray-600 mt-1">{t('oneToOneCalls.subheading')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          {renderInlineStats()}
          {activeTab !== 'widget' && (
            <div className="flex items-center border border-gray-300 rounded-lg p-1 bg-white">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                title={t('oneToOneCalls.viewMode.card')}
                aria-label={t('oneToOneCalls.viewMode.card')}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                title={t('oneToOneCalls.viewMode.list')}
                aria-label={t('oneToOneCalls.viewMode.list')}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Navigation tabs */}
      <nav className="flex space-x-1 bg-gray-200 rounded-xl p-1 mb-6">
        <button
          onClick={() => setActiveTab('our_requests')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${activeTab === 'our_requests'
              ? 'bg-[#4339A0] text-white shadow-md'
              : 'text-gray-600 hover:text-gray-900 hover:bg-white hover:bg-opacity-50'
            }`}
        >
          <Send className="w-4 h-4" />
          <span>{loadingRequests ? t('oneToOneCalls.tabs.ourRequests') : t('oneToOneCalls.tabs.ourRequestsWithCount', { count: ourCallRequests.length })}</span>
        </button>

        <button
          onClick={() => setActiveTab('promoter_submissions')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${activeTab === 'promoter_submissions'
              ? 'bg-[#4339A0] text-white shadow-md'
              : 'text-gray-600 hover:text-gray-900 hover:bg-white hover:bg-opacity-50'
            }`}
        >
          <Phone className="w-4 h-4" />
          <span>{loadingSubmissions ? t('oneToOneCalls.tabs.promoterSubmissions') : t('oneToOneCalls.tabs.promoterSubmissionsWithCount', { count: allPromoterSubmissions.length })}</span>
        </button>

        <button
          onClick={() => setActiveTab('activated')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${activeTab === 'activated'
              ? 'bg-[#4339A0] text-white shadow-md'
              : 'text-gray-600 hover:text-gray-900 hover:bg-white hover:bg-opacity-50'
            }`}
        >
          <CheckCircle className="w-4 h-4" />
          <span>{loadingPromoters ? t('oneToOneCalls.tabs.activated') : t('oneToOneCalls.tabs.activatedWithCount', { count: allActivatedPromoters.length })}</span>
        </button>

        <button
          disabled
          type="button"
          className="flex items-center space-x-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 opacity-60 cursor-not-allowed pointer-events-none text-gray-500 bg-gray-100"
        >
          <Target className="w-4 h-4" />
          <span>{t('oneToOneCalls.tabs.widget')}</span>
        </button>
      </nav>

      <div>
        {activeTab === 'our_requests' && renderOurRequests()}
        {activeTab === 'promoter_submissions' && renderPromoterSubmissions()}
        {activeTab === 'activated' && renderActivated()}
        {activeTab === 'widget' && renderWidget()}
      </div>
    </div>
  );
};

export default OneToOneCalls;